import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, BarChart3, Users, Gift, ShieldCheck, Settings } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { BrandMark } from "./BrandMark";
import { BrandMarkLink } from "./BrandMarkLink";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", Icon: BarChart3, testId: "admin-nav-dashboard" },
  { to: "/admin/customers", label: "Customers", Icon: Users, testId: "admin-nav-customers" },
  { to: "/admin/rewards", label: "Rewards", Icon: Gift, testId: "admin-nav-rewards" },
  { to: "/admin/staff", label: "Staff", Icon: ShieldCheck, testId: "admin-nav-staff" },
  { to: "/admin/settings", label: "Settings", Icon: Settings, testId: "admin-nav-settings" },
];

const initialsOf = (user) => {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "A";
  return (user?.email || "A").charAt(0).toUpperCase();
};

const TopLink = ({ to, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "relative font-body font-medium text-[13px] tracking-[0.15em] uppercase px-1 py-2 transition-colors duration-200",
        isActive ? "text-napoli-oxblood" : "text-napoli-espresso-soft hover:text-napoli-espresso",
      ].join(" ")
    }
  >
    {({ isActive }) => (
      <>
        {label}
        {isActive ? (
          <span aria-hidden className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 h-[2px] w-8 bg-napoli-oxblood" />
        ) : null}
      </>
    )}
  </NavLink>
);

export const AdminTopNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = initialsOf(user);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Owner";

  return (
    <header
      data-testid="admin-top-nav"
      className="hidden md:block sticky top-0 z-30 bg-napoli-marble border-b border-napoli-gold/20"
    >
      <div className="max-w-7xl mx-auto px-8 h-[80px] flex items-center gap-8">
        <div className="flex items-center gap-3">
          <BrandMarkLink size="sm" theme="light" testId="admin-topnav-brand" ariaLabel="Napoli — admin portal" />
          <span
            data-testid="admin-topnav-admin-badge"
            className="hidden lg:inline-flex items-center px-2.5 py-1 bg-napoli-cream-warm border border-napoli-oxblood/40 text-napoli-oxblood font-body font-semibold text-[10px] tracking-[0.3em] uppercase"
          >
            Admin
          </span>
        </div>

        <nav className="flex-1 flex items-center justify-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <TopLink key={l.to} {...l} />
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="admin-topnav-account-trigger"
                className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-napoli-oxblood/50 rounded-full"
              >
                <span className="hidden xl:inline font-body text-[12px] text-napoli-espresso-soft">
                  {fullName}
                </span>
                <span
                  aria-hidden
                  className="h-9 w-9 rounded-full bg-napoli-cream-warm border-2 border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-sm group-hover:bg-napoli-cream transition-colors"
                >
                  {initials}
                </span>
                <ChevronDown size={14} className="text-napoli-espresso-soft group-hover:text-napoli-espresso transition-colors" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              className="min-w-[220px] bg-napoli-marble border-napoli-gold/30 rounded-none p-1"
              data-testid="admin-topnav-account-menu"
            >
              <DropdownMenuLabel className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft px-3 py-2">
                {fullName} · Admin
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-napoli-gold/20" />
              <DropdownMenuItem
                data-testid="admin-topnav-menu-logout"
                onSelect={() => logout("/")}
                className="font-body text-[13px] text-napoli-oxblood focus:bg-napoli-cream-warm focus:text-napoli-oxblood-dark rounded-none cursor-pointer px-3 py-2.5"
              >
                <LogOut size={16} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default AdminTopNav;
